import * as React from 'react';
import { useQuery, Direction } from './Query';
import { FilterHeader } from './QueryTable';
import Modal, { ModalSize } from '../misc/components/modal';
import Select from 'react-select';

export interface Option {
    label: any;
    value: any;
}

interface OverreadStatusHeaderProps {
    title?: string;
    orderField: string;
    overreadStatuses: Option[];
    overreadChallengeStatuses: Option[];
    overreadAnalysisSetTypeValue?: string;
    challengeField?: string;
    sortField?: string;
    automationId?: string;
    useStatusField?: boolean;
}

export const OverreadStatusHeader = ({ title, orderField, overreadStatuses, overreadChallengeStatuses, overreadAnalysisSetTypeValue, challengeField, useStatusField = false, sortField = null, automationId = null }: OverreadStatusHeaderProps) => {

    const [display, setDisplay] = React.useState(false);
    const { filters, ordering, applyOrdering } = useQuery();
    const [ordered, setOrdered] = React.useState<Direction>();
    const [filtered, setFiltered] = React.useState(false);
    sortField = sortField ?? orderField;
    title = title ?? "Status";
    challengeField = challengeField ?? "overreadChallengeStatus";

    React.useEffect(() => {
        const filtered = filters.some(f => (f.field === 'analysisSets' && f.condition === 'any' && f.subQueryConditions?.find(sqc => sqc.value === overreadAnalysisSetTypeValue)) 
            || (useStatusField ? f.field === 'status' && f.condition === 'eq' : false) 
            || (f.field === 'overreadChallengeStatus' && f.condition === 'eq'));
        setFiltered(filtered);
    }, [filters]);

    React.useEffect(() => {
        const ordered = ordering.find(o => o.field === sortField);
        setOrdered(ordered
            ? ordered.condition
            : null);
    }, [ordering]);

    const handleSort = () => {
        let direction: Direction = 'asc';

        if (ordered === 'asc')
            direction = 'desc';

        applyOrdering([{ field: sortField, condition: direction }]);
    }

    const handleFilter = () => {
        setDisplay(true);
    }

    const modal = display
        ? (
            <OverreadStatusFilterModal
                title={title}
                overreadStatuses={overreadStatuses}
                overreadChallengeStatuses={overreadChallengeStatuses}
                overreadAnalysisSetTypeValue={overreadAnalysisSetTypeValue}
                challengeField={challengeField}
                useStatusField={useStatusField}
                onClose={() => setDisplay(false)} />
        )
        : null;

    return (
        <>
            <FilterHeader automationId={automationId} filtered={filtered} sorted={ordered} onFilter={handleFilter} onSort={handleSort}>{title}</FilterHeader>
            {modal}
        </>
    );
}

interface OverreadStatusFilterModalProps {
    title?: string;
    overreadStatuses: any[];
    overreadChallengeStatuses: any[];
    overreadAnalysisSetTypeValue?: string;
    challengeField: string;
    useStatusField?: boolean;
    onClose: () => void;
}

export const OverreadStatusFilterModal = ({ title, overreadStatuses, overreadChallengeStatuses, onClose, overreadAnalysisSetTypeValue, challengeField, useStatusField = false }: OverreadStatusFilterModalProps) => {

    const [statusValues, setStatusValues] = React.useState<any[]>([]);
    const [challengeValues, setChallengeValues] = React.useState<any[]>([]);

    const { filters, applyFilters, paging, applyPaging } = useQuery();

    title = title ?? "Status";

    const analysisSetsField = 'analysisSets'
        , analysisSetsSubField = 'status'
        , analysisSetTypeField = 'type'
        , analysisSetType = overreadAnalysisSetTypeValue ?? 'SAS_OVERREAD';

    React.useEffect(() => {
        const currentStatusValues = useStatusField 
            ? filters.filter(f => f.field === 'status' && f.condition === 'eq').flatMap(f => f.values)
            : filters.filter(f => f.field === analysisSetsField && f.condition === 'any' && f.subQueryConditions?.find(sqc => sqc.value === overreadAnalysisSetTypeValue)).flatMap(f => f.values);

        const currentChallengeValues = filters
            .filter(f => f.field === challengeField && f.condition === 'eq')
            .flatMap(f => f.values);

        setStatusValues(currentStatusValues);
        setChallengeValues(currentChallengeValues);
    }, [filters]);


    const handleApply = () => {
        let newFilters = useStatusField 
            ? filters.filter(f => !(f.field === 'status' && f.condition === 'eq') && f.field !== challengeField)
            : filters.filter(f => !(f.field === analysisSetsField
                && f.primarySubClauseField === analysisSetsSubField
                && f.subQueryConditions?.find(sqc => sqc.value === overreadAnalysisSetTypeValue))
                && f.field !== challengeField);

        statusValues && statusValues.length > 0 && (useStatusField 
            ? newFilters = [...newFilters,
                {
                    field: 'status',
                    condition: 'eq',
                    values: statusValues
                }
            ]
            : newFilters = [...newFilters,
                {
                    field: analysisSetsField,
                    condition: 'any',
                    primarySubClauseField: analysisSetsSubField,
                    primarySubQueryCondition: 'eq',
                    values: statusValues,
                    subQueryConditions: [
                        {
                            subClauseField: analysisSetTypeField,
                            subCondition: 'eq',
                            value: analysisSetType
                        },
                    ]
                }
            ]);

        challengeValues && challengeValues.length > 0 && (
            newFilters = [...newFilters, {
                field: challengeField,
                condition: 'eq',
                values: challengeValues,
            }]);

        applyPaging({ ...paging, page: 1 });
        applyFilters(newFilters);
        onClose();
    }

    const handleClear = () => {
        applyPaging({ ...paging, page: 1 });

        applyFilters(filters.filter(
            f =>
                (
                    f.field !== analysisSetsField
                )
                &&
                (
                    f.field !== 'status'
                )
                &&
                (
                    f.field !== challengeField
                )
        ));

        onClose();
    }

    return (
        <Modal header={`${title} Filter`} size={ModalSize.Normal} show={true} closeModalHandler={onClose}>
            <div className="modal-body">
                <label>Status</label>
                <Select
                    id="overread-status-match"
                    name="match1"
                    isMulti={true}
                    classNamePrefix="multiSelect"
                    options={overreadStatuses}
                    autoFocus={true}
                    value={statusValues.map(v => overreadStatuses.find(o => o.value === v))}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => o.value}
                    onChange={(o: any[]) => setStatusValues(o.map(v => v.value))}
                />
            </div>
            <div className="modal-body">
                <label>Challenge Status</label>
                <Select
                    name="match2"
                    id="overread-challenge-status-match"
                    isMulti={true}
                    classNamePrefix="multiSelect"
                    options={overreadChallengeStatuses}
                    autoFocus={true}
                    value={challengeValues.map(v => overreadChallengeStatuses.find(o => o.value === v))}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => o.value}
                    onChange={(o: any[]) => setChallengeValues(o.map(v => v.value))}
                />
            </div>
            <div className="modal-footer">
                <button className="btn btn-primary" onClick={handleApply} data-automationid="submit-filter">OK</button>
                <button className="btn btn-danger" onClick={handleClear} data-automationid="clear-filter">Clear</button>
            </div>
        </Modal>
    );
};
